import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

interface InViewOptions {
    once?: boolean
    rootMargin?: string
    threshold?: number
}

/**
 * Reports whether an element is on screen. With `once`, it stays true after the first sighting.
 */
export const useInView = <T extends Element>(options: InViewOptions = {}): [RefObject<T | null>, boolean] => {
    const ref = useRef<T>(null)
    const [inView, setInView] = useState(false)
    const { once = true, rootMargin = '0px 0px -10% 0px', threshold = 0 } = options

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const observer = new IntersectionObserver(entries => {
            const visible = entries.some(entry => entry.isIntersecting)

            if (visible) {
                setInView(true)

                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin, threshold })

        observer.observe(element)

        return () => observer.disconnect()
    }, [once, rootMargin, threshold])

    return [ref, inView]
}
